import { useEffect, useRef } from 'react';
import socket from '../socket';
import CLIENT_ID from '../utils/clientId';

/**
 * useCollabSync — receives everything the server accepted from other editors.
 *
 * This is the only way a change from another client now arrives. The editor used
 * to relay its own mutations peer-to-peer, which meant every client was asserting
 * things about a document it did not own; the server rebroadcasts after the write
 * is durable instead, so what arrives here is always something that actually
 * happened, with the version number to prove where it sits in the sequence.
 *
 * Three events, one per thing that can change under an editor's feet:
 *
 *   canvasOpsApplied   — the draft graph. Applied by `applyRemoteOps`
 *                        (hooks/useCanvasOps.js), the same pure appliers that run
 *                        for a local gesture.
 *   canvasPublished    — somebody published. Publishing does not bump `version`,
 *                        so nothing about it travels through the op stream; without
 *                        this event a collaborator's status pill read "Unpublished
 *                        changes" for the rest of their session.
 *   scenarioMetaUpdated — the scenario's own fields (title, published state), which
 *                        live outside the canvas entirely.
 *
 * Callbacks are held in refs so a caller can pass inline arrows without
 * resubscribing the socket on every render — only `scenarioId` and `enabled`
 * decide whether we are listening at all.
 *
 * @param {Object} params
 * @param {string} params.scenarioId
 * @param {boolean} [params.enabled] - False for read-only viewers.
 * @param {Function} params.applyRemoteOps
 * @param {Function} [params.onPublished] - `({publishedVersion, scenario, userId}) => void`
 * @param {Function} [params.onScenarioMeta] - `({fields, userId}) => void`
 */
export default function useCollabSync({
    scenarioId,
    enabled = true,
    applyRemoteOps,
    onPublished,
    onScenarioMeta,
} = {}) {
    const applyRemoteOpsRef = useRef(applyRemoteOps);
    const onPublishedRef = useRef(onPublished);
    const onScenarioMetaRef = useRef(onScenarioMeta);

    applyRemoteOpsRef.current = applyRemoteOps;
    onPublishedRef.current = onPublished;
    onScenarioMetaRef.current = onScenarioMeta;

    useEffect(() => {
        if (!enabled || !scenarioId) return undefined;

        /**
         * The room is per scenario, but a socket that just switched scenarios can
         * still see one late frame from the old one; and our own writes come back
         * to us too. Matched by TAB, not by user: the same author's other tab is a
         * different copy of the document and must be updated like anyone else.
         */
        const isForUs = (payload) => {
            if (payload.scenarioId && payload.scenarioId !== scenarioId) return false;
            if (payload.clientId && payload.clientId === CLIENT_ID) return false;
            return true;
        };

        const handleOpsApplied = (payload = {}) => {
            if (!isForUs(payload)) return;
            applyRemoteOpsRef.current?.(payload);
        };

        const handlePublished = (payload = {}) => {
            if (!isForUs(payload)) return;
            onPublishedRef.current?.(payload);
        };

        const handleScenarioMeta = (payload = {}) => {
            if (!isForUs(payload)) return;
            onScenarioMetaRef.current?.(payload);
        };

        socket.on('canvasOpsApplied', handleOpsApplied);
        socket.on('canvasPublished', handlePublished);
        socket.on('scenarioMetaUpdated', handleScenarioMeta);

        return () => {
            socket.off('canvasOpsApplied', handleOpsApplied);
            socket.off('canvasPublished', handlePublished);
            socket.off('scenarioMetaUpdated', handleScenarioMeta);
        };
    }, [scenarioId, enabled]);
}
